import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type {
  PayrollBatchDto,
  PayrollBatchLineInputDto,
} from "@/features/payroll/api/dto";

/**
 * Ajoute un salarié à une paie `DRAFT` ou `SIMULATED` (`POST
 * /PayrollBatches/{id}/lines`) → la paie revient en `DRAFT`. Codes : 200 ;
 * 404 ; 422 (paie lancée ou 1000 lignes atteintes).
 */
export const useAddPayrollLine = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "addLine", batchId],
    mutationFn: (body: PayrollBatchLineInputDto) =>
      mutateData({
        url: payrollEndpoints.payrollBatches.addLine(batchId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as unknown as Promise<PayrollBatchDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useAddPayrollLine failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
